// The questionnaire card as the client sees it. It owns what is being
// typed, the ticked agreements and a refused file; the answers, the files,
// what is still needed and every write come from the caller.

import { useState } from "react";
import type {
  IntakeAnswerValue,
  IntakeLanguage,
  IntakeQuestion,
  IntakeQuestionnaireState,
  IntakeTemplate,
  IntakeUploadedDocument,
} from "@duralex/shared";
import { UPLOAD_ACCEPT, uploadProblem } from "../../../api/documents";
import { applicableChecklist, applicableQuestions, isRequired } from "../lib/intakeLogic";
import { languageAutonym, textIn } from "../../../lib/intakeLanguages";

export type FieldNote = { kind: "saved" | "error"; message?: string };

interface Props {
  template: IntakeTemplate;
  locale: IntakeLanguage;
  languages: IntakeLanguage[];
  /** The page's own words, by key. */
  t: (key: string) => string;
  category: string | null;
  categoryLocked: boolean;
  answers: Record<string, IntakeAnswerValue>;
  /** Multichoice boxes as the client ticked them, shown over the saved list. */
  ticks: Record<string, string[]>;
  documents: IntakeUploadedDocument[];
  outstanding: IntakeQuestionnaireState["outstanding"];
  /** Per field or picker: how its last save ended. */
  notes: Record<string, FieldNote>;
  /** ISO instant; without it the "works until" line is not drawn. */
  expiresAt?: string;
  saving: boolean;
  uploading: boolean;
  removing: boolean;
  submitting: boolean;
  alerts: { upload?: string; remove?: string; submit?: string };
  onLanguage: (locale: IntakeLanguage) => void;
  onCategory: (code: string) => void;
  onAnswer: (id: string, value: IntakeAnswerValue) => void;
  onUpload: (item: string, file: File) => void;
  onRemoveDoc: (id: string) => void;
  onSubmit: (consents: string[]) => void;
}

export function QuestionnaireForm({
  template,
  locale,
  languages,
  t,
  category,
  categoryLocked,
  answers,
  ticks,
  documents,
  outstanding,
  notes,
  expiresAt,
  saving,
  uploading,
  removing,
  submitting,
  alerts,
  onLanguage,
  onCategory,
  onAnswer,
  onUpload,
  onRemoveDoc,
  onSubmit,
}: Props) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [consents, setConsents] = useState<string[]>([]);
  const [uploadWarn, setUploadWarn] = useState<string | null>(null);

  const questions = applicableQuestions(template, category);
  const byId = new Map(questions.map((q) => [q.id, q]));
  const checklist = applicableChecklist(template, category);
  // A file uploaded under a checklist item the current category no longer
  // asks for still belongs to the client: it shows under "other".
  const knownItems = new Set(checklist.map((c) => c.id));
  const filesOf = (item: string | null) =>
    item === null
      ? documents.filter((d) => d.item === "other" || !knownItems.has(d.item))
      : documents.filter((d) => d.item === item);
  const chosen = template.categories.find((c) => c.code === category);
  const allConsents = template.consents.every((c) => consents.includes(c.id));

  const storedText = (id: string): string => {
    const v = answers[id];
    return typeof v === "string" ? v : "";
  };
  const saveField = (id: string, value: string) => {
    if (value === storedText(id)) return;
    onAnswer(id, value);
  };
  const note = (key: string) => {
    const n = notes[key];
    if (!n) return null;
    return n.kind === "saved" ? (
      <span className="iform-saved">{t("saved")}</span>
    ) : (
      <span className="iform-q-error" role="alert">
        {n.message}
      </span>
    );
  };

  const input = (q: IntakeQuestion) => {
    const draft = drafts[q.id] ?? storedText(q.id);
    const onDraft = (v: string) => setDrafts((d) => ({ ...d, [q.id]: v }));
    switch (q.type) {
      case "longtext":
        return (
          <textarea
            className="cc-field"
            aria-label={textIn(q.label, locale)}
            rows={4}
            maxLength={5000}
            value={draft}
            onChange={(e) => onDraft(e.target.value)}
            onBlur={() => saveField(q.id, draft.trim())}
          />
        );
      case "date":
        return (
          <input
            className="cc-field"
            aria-label={textIn(q.label, locale)}
            type="date"
            value={draft}
            onChange={(e) => onDraft(e.target.value)}
            onBlur={() => saveField(q.id, draft)}
          />
        );
      case "yesno":
        return (
          <div className="iform-choices">
            {(["yes", "no"] as const).map((v) => (
              <label key={v} className="iform-choice">
                <input
                  type="radio"
                  name={q.id}
                  checked={answers[q.id] === v}
                  onChange={() => onAnswer(q.id, v)}
                />
                <span>{t(v)}</span>
              </label>
            ))}
          </div>
        );
      case "choice":
        return (
          <div className="iform-choices">
            {(q.options ?? []).map((o) => (
              <label key={o.value} className="iform-choice">
                <input
                  type="radio"
                  name={q.id}
                  checked={answers[q.id] === o.value}
                  onChange={() => onAnswer(q.id, o.value)}
                />
                <span>{textIn(o.label, locale)}</span>
              </label>
            ))}
          </div>
        );
      case "multichoice": {
        const stored = Array.isArray(answers[q.id]) ? (answers[q.id] as string[]) : [];
        const picked = ticks[q.id] ?? stored;
        const toggle = (v: string) => {
          onAnswer(q.id, picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v]);
        };
        return (
          <div className="iform-choices">
            {(q.options ?? []).map((o) => (
              <label key={o.value} className="iform-choice">
                <input
                  type="checkbox"
                  checked={picked.includes(o.value)}
                  onChange={() => toggle(o.value)}
                />
                <span>{textIn(o.label, locale)}</span>
              </label>
            ))}
          </div>
        );
      }
      default:
        return (
          <input
            className="cc-field"
            aria-label={textIn(q.label, locale)}
            maxLength={500}
            value={draft}
            onChange={(e) => onDraft(e.target.value)}
            onBlur={() => saveField(q.id, draft.trim())}
          />
        );
    }
  };

  return (
    <div className="iform-card" lang={locale}>
      <div className="iform-head">
        <h1>{t("title")}</h1>
        <p>{t("intro")}</p>
        {expiresAt && (
          <div className="iform-expires">
            {t("expires")}{" "}
            {new Date(expiresAt).toLocaleDateString(locale === "en" ? "en-US" : locale, {
              month: "long",
              day: "numeric",
            })}
          </div>
        )}
      </div>

      <div className="iform-controls">
        <label className="cc-form-field">
          <span className="cc-form-label">{t("language")}</span>
          <select
            className="cc-field"
            aria-label={t("language")}
            value={locale}
            onChange={(e) => onLanguage(e.target.value as IntakeLanguage)}
          >
            {languages.map((l) => (
              <option key={l} value={l} lang={l}>
                {languageAutonym(l)}
              </option>
            ))}
          </select>
          {note("language")}
        </label>
        <label className="cc-form-field">
          <span className="cc-form-label">{t("case_type")}</span>
          {categoryLocked ? (
            <>
              <span className="iform-label">
                {chosen ? textIn(chosen.label, locale) : category}
              </span>
              <span className="cc-form-hint">{t("case_type_locked")}</span>
            </>
          ) : (
            <select
              className="cc-field"
              aria-label={t("case_type")}
              value={category ?? ""}
              onChange={(e) => e.target.value && onCategory(e.target.value)}
            >
              <option value="">{t("case_type_pick")}</option>
              {template.categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {textIn(c.label, locale)}
                </option>
              ))}
            </select>
          )}
          {note("category")}
        </label>
      </div>

      {template.sections.map((section) => {
        const qs = section.questions.filter((q) => byId.has(q.id));
        if (qs.length === 0) return null;
        return (
          <section key={section.id} className="iform-section">
            <div className="iform-section-title">{textIn(section.title, locale)}</div>
            {qs.map((q) => (
              <div key={q.id} className="iform-q">
                <span className="iform-label">
                  {textIn(q.label, locale)}
                  {isRequired(q, template, category) && (
                    <span className="req">{t("required")}</span>
                  )}
                </span>
                {q.help && <span className="iform-help">{textIn(q.help, locale)}</span>}
                {input(q)}
                {note(q.id)}
              </div>
            ))}
          </section>
        );
      })}

      <section className="iform-section">
        <div className="iform-section-title">{t("documents")}</div>
        <span className="iform-help">{t("documents_hint")}</span>
        {uploadWarn && (
          <div role="alert" className="cc-form-error">
            {uploadWarn}
          </div>
        )}
        {alerts.upload && (
          <div role="alert" className="cc-form-error">
            {alerts.upload}
          </div>
        )}
        {alerts.remove && (
          <div role="alert" className="cc-form-error">
            {alerts.remove}
          </div>
        )}
        {[...checklist, null].map((item) => (
          <div key={item?.id ?? "other"} className="iform-q">
            <span className="iform-label">
              {item ? textIn(item.label, locale) : t("other_documents")}
              {item && isRequired(item, template, category) && (
                <span className="req">{t("required")}</span>
              )}
            </span>
            <div className="iform-files">
              {filesOf(item ? item.id : null).map((d) => (
                <div key={d.id} className="iform-file">
                  <span className="name">{d.fileName}</span>
                  <button
                    type="button"
                    className="cc-link-btn danger"
                    disabled={removing}
                    onClick={() => onRemoveDoc(d.id)}
                  >
                    {t("remove")}
                  </button>
                </div>
              ))}
            </div>
            <label className="cc-link-btn">
              {uploading ? t("saving") : t("upload")}
              <input
                type="file"
                accept={UPLOAD_ACCEPT}
                className="sr-only"
                aria-label={`${t("upload")}: ${item ? textIn(item.label, locale) : t("other_documents")}`}
                disabled={uploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  const problem = uploadProblem(file);
                  if (problem) {
                    setUploadWarn(problem);
                    return;
                  }
                  setUploadWarn(null);
                  onUpload(item?.id ?? "other", file);
                }}
              />
            </label>
          </div>
        ))}
      </section>

      <section className="iform-section">
        {(outstanding.questions.length > 0 || outstanding.documents.length > 0) && (
          <div className="iform-outstanding">
            {outstanding.questions.length > 0 && (
              <>
                <strong>{t("outstanding")}</strong>
                <ul>
                  {outstanding.questions.map((id) => (
                    <li key={id}>{byId.get(id) ? textIn(byId.get(id)!.label, locale) : id}</li>
                  ))}
                </ul>
              </>
            )}
            {outstanding.documents.length > 0 && (
              <>
                <strong>{t("outstanding_docs")}</strong>
                <ul>
                  {outstanding.documents.map((id) => {
                    const item = checklist.find((c) => c.id === id);
                    return <li key={id}>{item ? textIn(item.label, locale) : id}</li>;
                  })}
                </ul>
              </>
            )}
          </div>
        )}

        <div className="iform-section-title">{t("consents")}</div>
        {template.consents.map((c) => (
          <label key={c.id} className="iform-consent">
            <input
              type="checkbox"
              checked={consents.includes(c.id)}
              onChange={(e) =>
                setConsents((prev) =>
                  e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id),
                )
              }
            />
            <span>{textIn(c.text, locale)}</span>
          </label>
        ))}
        {alerts.submit && (
          <div role="alert" className="cc-form-error">
            {alerts.submit}
          </div>
        )}
        {category === null && <span className="cc-form-hint">{t("case_type_pick")}</span>}
        <div className="cc-form-actions">
          <button
            type="button"
            className="cc-btn-primary"
            disabled={
              submitting ||
              saving ||
              !allConsents ||
              category === null ||
              outstanding.questions.length > 0
            }
            onClick={() => onSubmit(consents)}
          >
            {submitting ? t("submitting") : saving ? t("saving") : t("submit")}
          </button>
        </div>
      </section>
    </div>
  );
}
